import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import type { TimeChipsProps } from "./types";

// Format time from Date to display format (e.g., "8:00 AM")
function formatTimeDisplay(date: Date, use24h: boolean): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();

  if (use24h) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  const h12 = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  return `${h12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
}

export function TimeChips({ value, showDuration = true }: TimeChipsProps) {
  const { colors } = useTheme();
  const { settings, toUserTimezone } = useDateTime();
  const use24h = settings.timeFormat === "24";

  const { startDisplay, endDisplay, workedHoursDisplay, mealHoursDisplay, breakHoursDisplay, nonWorkedHoursDisplay } =
    useMemo(() => {
      if (!value?.segments || value.segments.length === 0 || !value.start_time) {
        return {
          startDisplay: null,
          endDisplay: null,
          workedHoursDisplay: null,
          mealHoursDisplay: null,
          breakHoursDisplay: null,
          nonWorkedHoursDisplay: null,
        };
      }

      const segments = value.segments;
      const globalStart = toUserTimezone(new Date(value.start_time));

      // Calculate end time from cumulative durations
      const totalDurationHours = segments.reduce((sum, seg) => sum + seg.duration, 0);
      const endTime = new Date(globalStart.getTime() + totalDurationHours * 60 * 60 * 1000);

      // Calculate hours by type
      let workedHours = 0;
      let mealHours = 0;
      let breakHours = 0;
      let nonWorkedHours = 0;

      for (const segment of segments) {
        if (segment.type === "worked") {
          workedHours += segment.duration;
        } else if (segment.type === "meal") {
          mealHours += segment.duration;
        } else if (segment.type === "break") {
          breakHours += segment.duration;
        } else {
          nonWorkedHours += segment.duration;
        }
      }

      return {
        startDisplay: formatTimeDisplay(globalStart, use24h),
        endDisplay: formatTimeDisplay(endTime, use24h),
        workedHoursDisplay: workedHours > 0 ? `${workedHours.toFixed(1)} hrs` : null,
        mealHoursDisplay: mealHours > 0 ? `M ${mealHours.toFixed(1)}` : null,
        breakHoursDisplay: breakHours > 0 ? `B ${breakHours.toFixed(1)}` : null,
        nonWorkedHoursDisplay: nonWorkedHours > 0 ? `NW ${nonWorkedHours.toFixed(1)}` : null,
      };
    }, [value, use24h, toUserTimezone]);

  if (!value?.segments || value.segments.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      {startDisplay && (
        <View style={[styles.chip, { backgroundColor: colors.complete?.border || "#10b981" }]}>
          <ThemedText style={styles.chipText}>{startDisplay}</ThemedText>
        </View>
      )}
      {endDisplay && (
        <View style={[styles.chip, { backgroundColor: colors.attention?.border || "#ef4444" }]}>
          <ThemedText style={styles.chipText}>{endDisplay}</ThemedText>
        </View>
      )}
      {showDuration && workedHoursDisplay && (
        <View style={[styles.chip, { backgroundColor: colors.neutral?.border || "#6b7280" }]}>
          <ThemedText style={styles.chipText}>{workedHoursDisplay}</ThemedText>
        </View>
      )}
      {mealHoursDisplay && (
        <View style={[styles.chip, styles.smallChip, { backgroundColor: colors.pending?.border || "#f59e0b" }]}>
          <ThemedText style={styles.chipText}>{mealHoursDisplay}</ThemedText>
        </View>
      )}
      {breakHoursDisplay && (
        <View style={[styles.chip, styles.smallChip, { backgroundColor: colors.neutral?.border || "#6b7280" }]}>
          <ThemedText style={styles.chipText}>{breakHoursDisplay}</ThemedText>
        </View>
      )}
      {nonWorkedHoursDisplay && (
        <View style={[styles.chip, styles.smallChip, { backgroundColor: colors.primary || "#3b82f6" }]}>
          <ThemedText style={styles.chipText}>{nonWorkedHoursDisplay}</ThemedText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 4,
    alignItems: "center",
    overflow: "visible",
    flex: 1,
  },
  chip: {
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: 10,
  },
  chipText: {
    fontSize: 11,
    color: "white",
    fontWeight: "500",
  },
  smallChip: {
    paddingHorizontal: 5,
  },
});
